import { useCallback, useRef } from 'react';
import useTracking from 'src/hooks/useTracking';
import {
  DatahubEntity,
  FullscreenAction,
  PlaybackSpeedAction,
  PictureInPictureAction,
  PlaybackAction,
  ResolutionAction,
  LoadedMetadataAction,
  FirstplayAction,
} from 'src/interfaces/Datahub';
import { EVENT_TYPES } from 'src/interfaces/enums';
import { MessageEventType } from 'src/interfaces/MessageEvent';
import ApplicationService from 'src/services/ApplicationService';
import { debounce } from 'src/utils/debounceUtils';
import useTrackWatchTime from '../useTrackWatchTime';

function useTrackingEvents() {
  const { trackDatahub, trackProgress, trackMessageEvent } = useTracking();
  const { createWatchtimeListeners } = useTrackWatchTime();
  const currentTimeRef = useRef(0); 

  const createTrackingListeners = useCallback(() => {
    const player = ApplicationService.player;

    if (!player) return;

    createWatchtimeListeners();
    
    player.addEventListener(EVENT_TYPES.TIME_UPDATE, () => {
      const currentTime = Math.floor(ApplicationService.player?.currentTime || 0);

      if (currentTime !== currentTimeRef.current) {
        trackMessageEvent(MessageEventType.PROGRESS);
        currentTimeRef.current = currentTime;
      }
    });

    player.addEventListener(EVENT_TYPES.FULLSCREEN_CHANGE, () => {
      trackDatahub(DatahubEntity.FULLSCREEN, FullscreenAction.CHANGE);
    });

    player.addEventListener(EVENT_TYPES.RATE_CHANGE, () => {
      trackDatahub(DatahubEntity.PLAYBACK_SPEED, PlaybackSpeedAction.CHANGE);
    });

    player.addEventListener(EVENT_TYPES.ENTERPICTUREINPICTURE, () => {
      trackDatahub(DatahubEntity.PICTURE_IN_PICTURE, PictureInPictureAction.CHANGE);
    });

    player.addEventListener(EVENT_TYPES.LEAVEPICTUREINPICTURE, () => {
      trackDatahub(DatahubEntity.PICTURE_IN_PICTURE, PictureInPictureAction.CHANGE);
    });

    player.addEventListener(
      EVENT_TYPES.PLAY,
      debounce(() => trackDatahub(DatahubEntity.PLAYBACK, PlaybackAction.PLAY), 500),
    );
    player.addEventListener(EVENT_TYPES.PAUSE, () => {
      trackProgress();
      trackMessageEvent(MessageEventType.PAUSE);
    });

    player.addEventListener(
      EVENT_TYPES.PAUSE,
      debounce(() => trackDatahub(DatahubEntity.PLAYBACK, PlaybackAction.PAUSE), 500),
    );

    player.addEventListener(EVENT_TYPES.READY, () => {
      trackMessageEvent(MessageEventType.READY);
    });

    player.addEventListener(EVENT_TYPES.PLAY, () => {
      trackMessageEvent(MessageEventType.PLAY);
    });

    player.addEventListener(EVENT_TYPES.RESOLUTION_CHANGE, () => {
      trackDatahub(DatahubEntity.RESOLUTION, ResolutionAction.CHANGE);
    });

    player.addEventListener(EVENT_TYPES.LOADED_METADATA, () => {
      trackDatahub(DatahubEntity.LOADEDMETADATA, LoadedMetadataAction.MEASURE);
    });

    player.addEventListener(EVENT_TYPES.FIRST_PLAY, () => {
      trackDatahub(DatahubEntity.FIRSTPLAY, FirstplayAction.MEASURE);
    });

    player.addEventListener(EVENT_TYPES.ENDED, () => {
      trackProgress();
      trackMessageEvent(MessageEventType.ENDED);
    });
  }, []);

  return { createTrackingListeners };
}

export default useTrackingEvents;
